/**
 * ThemeModeProvider (TMMAPP-0001). Persists the user's mode preference under the storage key
 * `tmm_theme_mode` and resolves `system` via `matchMedia('(prefers-color-scheme: dark)')`, with a
 * live listener so the app follows an OS theme change. Both storage and matchMedia access are
 * try/catch-guarded: private mode (storage throws) falls back to the `system` default without
 * persisting; no `matchMedia` (bare jsdom) resolves to `light`. `system` is the default.
 */
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { ThemeMode } from '@/theme/tokens';
import {
  ThemeModeContext,
  type ThemeModeContextValue,
  type ThemeModePreference,
} from './theme-mode-context';

export const THEME_MODE_STORAGE_KEY = 'tmm_theme_mode';

function readStoredMode(): ThemeModePreference {
  try {
    const stored = localStorage.getItem(THEME_MODE_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    /* localStorage unavailable (e.g. private mode) — fall through to default */
  }
  return 'system';
}

function getSystemMode(): ThemeMode {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    /* matchMedia unavailable — fall through to light */
  }
  return 'light';
}

export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const [themeMode, setThemeModeState] = useState<ThemeModePreference>(readStoredMode);
  const [systemMode, setSystemMode] = useState<ThemeMode>(getSystemMode);

  useEffect(() => {
    try {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = (e: MediaQueryListEvent) => setSystemMode(e.matches ? 'dark' : 'light');
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    } catch {
      /* matchMedia unavailable — no system-theme listener */
    }
    return undefined;
  }, []);

  function setThemeMode(mode: ThemeModePreference) {
    setThemeModeState(mode);
    try {
      localStorage.setItem(THEME_MODE_STORAGE_KEY, mode);
    } catch {
      /* localStorage unavailable — preference not persisted */
    }
  }

  const resolvedMode: ThemeMode = themeMode === 'system' ? systemMode : themeMode;

  const value = useMemo<ThemeModeContextValue>(
    () => ({ themeMode, resolvedMode, setThemeMode }),
    [themeMode, resolvedMode],
  );

  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
}
